import axios from '@/plugins/axios'

// Members read the v2 route; `username` switches to its public twin, which the
// public collection page uses.
const boardPath = (collectionId, layout, username) =>
  username
    ? `/v1/public/u/${username}/collections/${collectionId}/${layout}`
    : `/v2/collections/${collectionId}/${layout}`

/**
 * A Board: the layout's resolved settings, the version's placements per Tier in
 * collection order, and the cards those placements show, side-loaded once.
 * `version` is a holder's userId or a BOARD_VIEW value (the Community
 * average, the comparison); without it the default rule picks the viewer's own
 * or the owner's version (ADR-0009).
 */
export const getBoard = (collectionId, { layout, username = null, version = null }) =>
  axios.get(boardPath(collectionId, layout, username), version ? { params: { version } } : undefined)

/**
 * One Move: `{ target, resourceType, resourceId, tierId, index }`, `tierId: null`
 * back to the Queue. Answers the Version's visible projection
 * `{ userId, versionId, rev, tiers }`. A PUT the axios interceptors never retry
 * on a network error — a stale re-send would be last-writer-wins.
 */
export const movePlacement = (collectionId, layout, body, { username = null } = {}) =>
  axios.put(`${boardPath(collectionId, layout, username)}/placements`, body)

/**
 * One page of the Versions a viewer can switch to: `{ results: [{ userId, user,
 * updatedAt, isOwn }], next, community }`. Page 1 (no `next`) starts with the
 * owner's row on top of `limit` member holders and carries the Community's
 * `{ count }` (null without one); `next` is opaque and goes back verbatim.
 */
export const getVersions = (collectionId, layout, { username = null, limit, next = null }) =>
  axios.get(`${boardPath(collectionId, layout, username)}/versions`, { params: { limit, ...(next && { next }) } })

/** Deletes the viewer's own version; answers it empty `{ userId, versionId: null, rev: 0, tiers: [] }`. */
export const resetOwnVersion = (collectionId, layout, { username = null } = {}) =>
  axios.delete(`${boardPath(collectionId, layout, username)}/versions/own`)

/**
 * Saves Layout settings: a non-empty subset of `{ tiers, participation, items }`,
 * each sent section replacing that section whole. Answers the layout's resolved
 * settings, new Tiers with their server-minted `_id`.
 */
export const patchLayoutSettings = (collectionId, layout, patch) =>
  axios.patch(`/v2/collections/${collectionId}/layouts/${layout}`, patch)
